import { Badge, Tooltip } from "@mantine/core";
import { useInterval } from "@mantine/hooks";
import dayjs, { Dayjs } from "dayjs";
// `z` (timezone abbreviation) is an advancedFormat token; without this plugin
// dayjs emits the literal character instead of "EDT"/"EST".
import advancedFormat from "dayjs/plugin/advancedFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { useEffect, useMemo, useState } from "react";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

/** Mirrors the backend digest cron: 4:00 PM in the business timezone. */
const DIGEST_HOUR = 16;
const BUSINESS_TIMEZONE = "America/New_York";
const COUNTDOWN_REFRESH_MS = 30_000;

export interface NextDigestEmail {
  label: string;
  explanation: string;
}

function nextDigestRun(from: Dayjs): Dayjs {
  const zoned = from.tz(BUSINESS_TIMEZONE);
  const todaysRun = zoned.hour(DIGEST_HOUR).minute(0).second(0).millisecond(0);
  return zoned.isBefore(todaysRun) ? todaysRun : todaysRun.add(1, "day");
}

function pluralize(value: number, unit: string): string {
  return `${value} ${unit}${value === 1 ? "" : "s"}`;
}

function formatCountdown(minutesRemaining: number): string {
  if (minutesRemaining < 1) return "in under a minute";
  const hours = Math.floor(minutesRemaining / 60);
  const minutes = minutesRemaining % 60;
  if (hours === 0) return `in ${pluralize(minutes, "minute")}`;
  if (minutes === 0) return `in ${pluralize(hours, "hour")}`;
  return `in ${pluralize(hours, "hour")} ${pluralize(minutes, "minute")}`;
}

interface PortalDigestEmailTimingBadgeProps {
  /**
   * Whether the report has actually reached the portal. False while the row is
   * still queued — including when a suppressed delivery method is holding it
   * back — so the tooltip never claims availability the recipient does not have.
   */
  reportAvailable?: boolean;
}

/**
 * The countdown is the reliable part: it reads the same however the viewer's
 * clock is set. The absolute time is always stamped with its zone, because the
 * digest runs on the lab's clock while the rest of the row renders in the
 * viewer's — an unlabelled "4:00 PM" would look like a contradiction.
 */
export function describeNextDigestEmail(
  from: Date,
  reportAvailable = true
): NextDigestEmail {
  const now = dayjs(from);
  const runsAt = nextDigestRun(now);
  const availability = reportAvailable
    ? "The report is on the portal now."
    : "This delivery has not reached the portal yet.";
  return {
    label: `Email ${formatCountdown(runsAt.diff(now, "minute"))}`,
    explanation: `${availability} Its daily summary email goes out ${runsAt.format(
      "[at] h:mm A z [on] MMM D"
    )}.`,
  };
}

export function PortalDigestEmailTimingBadge({
  reportAvailable = true,
}: PortalDigestEmailTimingBadgeProps) {
  const [now, setNow] = useState(() => new Date());
  const countdown = useInterval(() => setNow(new Date()), COUNTDOWN_REFRESH_MS);

  useEffect(() => {
    countdown.start();
    return countdown.stop;
  }, [countdown]);

  const { label, explanation } = useMemo(
    () => describeNextDigestEmail(now, reportAvailable),
    [now, reportAvailable]
  );

  return (
    <Tooltip label={explanation} multiline maw={320} withArrow>
      <Badge color="indigo" variant="light">
        {label}
      </Badge>
    </Tooltip>
  );
}
